/**
 * Générateur — Mobiliser un algorithme dans le cadre du calcul numérique.
 * Attendu ID : 909 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 7)
 *
 * Plan de cartes (proposé en autonomie, feuille de test à valider) :
 *
 *   Carte « recherche » — Ex.1 Recherche systématique des diviseurs
 *     Formats : texte_trou_drag (égalités N = k × … avec étiquette « impossible »),
 *     tableau_vf (« N = k × un nombre entier »), qcm_unique (à quelle ligne
 *     s'arrêter — question OUVERTE du doc fermée en QCM, distracteur clé :
 *     « dès qu'une égalité est impossible »), texte_trou_saisie (liste complète
 *     ordonnée, trous partiels), qcm_multiple (compte annoncé : « les trois
 *     diviseurs »). N tiré dans un pool contrôlé (≥ 3 lignes possibles et
 *     ≥ 2 impossibles dans la plage testée) ; plage glissante 1-6 → 4-9.
 *
 *   Carte « comptage » — Ex.2 Compter les diviseurs
 *     Formats : texte (saisie du compte), qcm_unique (misconceptions : oubli
 *     de 1 et n, oubli de n, racine comptée deux fois pour les carrés),
 *     texte_trou_saisie (étayage : liste puis compte — étayage cité par le
 *     doc), relier (nombre ↔ « k diviseurs », comptes deux à deux distincts).
 *     Le piège central du doc (25 : nombre IMPAIR de diviseurs) est dans le
 *     pool avec 16, 36 et 49.
 *
 *   Carte « tableur » — Ex.3 Division euclidienne avec un tableur (ADAPTÉ fermé)
 *     La feuille de calcul est reproduite en table HTML dans l'énoncé
 *     (pattern gen_675). Division construite : a = b×q + r avec 1 ≤ r < b —
 *     vraie par construction. Formats : texte_trou_saisie (égalité a = b×q+r),
 *     qcm_unique (formule du reste en D2 : =A2-B2×C2 contre les misconceptions
 *     du doc), tableau_vf (ce que MOD et QUOTIENT afficheront — valeurs
 *     calculées, 2 lignes de cours fixes garantissent le mélange V/F).
 *     Adaptation documentée : l'activité machine (1.b « ta calculatrice… »)
 *     est intraduisible en format fermé — évaluation après coup, comme le doc
 *     le prévoit.
 *
 *   Carte « script » — Ex.5 Pair ou impair (script façon Scratch)
 *     Le script est reproduit en SVG généré (blocs stylisés : chapeau jaune,
 *     demander bleu, si/sinon orange, hexagone vert « réponse modulo 2 = 0 »,
 *     dire violet) — le support visuel exigé par le doc. Formats :
 *     texte_trou_drag (compléter pair/impair, 2 formulations), tableau_vf
 *     (« si on entre 37, le script affichera… » — l'enrichissement suggéré),
 *     qcm_unique (exécution sur un nombre donné). Parité stricte (fidèle au
 *     doc) ; variabilité par prénoms + formulations + nombres testés.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 909;

const L_RECHERCHE = 'Rechercher tous les diviseurs (méthode systématique)';
const L_COMPTAGE = 'Compter les diviseurs d\'un nombre';
const L_TABLEUR = 'La division euclidienne avec un tableur';
const L_SCRIPT = 'Pair ou impair : lire un script (modulo)';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const PRENOMS = ['Léa', 'Tom', 'Inès', 'Noah', 'Zoé', 'Sami', 'Maya', 'Hugo', 'Lina', 'Enzo', 'Awa', 'Loïc'];

/** Tous les diviseurs de n, ordre croissant. */
const diviseursDe = (n) => {
    const d = [];
    for (let k = 1; k <= n; k++) if (n % k === 0) d.push(k);
    return d;
};

// ═════════════════════════════════════════════════════════════
// CARTE 1 — Recherche systématique des diviseurs (Ex.1)
// ═════════════════════════════════════════════════════════════

// Pool contrôlé : nombres riches en diviseurs ≤ 9 ET en lignes impossibles.
const POOL_RECHERCHE = [36, 42, 48, 54, 60, 63, 66, 70, 84, 90, 105];
const PLAGES = [[1, 6], [2, 7], [3, 8], [4, 9]];

/**
 * Tire (N, plage de k testés) avec ≥ 3 lignes possibles et ≥ 2 impossibles —
 * boucle bornée + repli déterministe vérifié (42, plage 2-7 : 4 poss, 2 imp).
 */
const tirerRecherche = () => {
    for (let essai = 0; essai < 40; essai++) {
        const N = randomChoice(POOL_RECHERCHE);
        const [k1, k2] = randomChoice(PLAGES);
        const ks = [];
        for (let k = k1; k <= k2; k++) ks.push(k);
        const poss = ks.filter(k => N % k === 0);
        const imp = ks.filter(k => N % k !== 0);
        if (poss.length >= 3 && imp.length >= 2) return { N, ks, poss, imp };
    }
    const ks = [2, 3, 4, 5, 6, 7];
    return { N: 42, ks, poss: [2, 3, 6, 7], imp: [4, 5] };
};

/** F1 — Drag : compléter chaque égalité par le quotient entier ou « impossible ». */
const genRechercheDrag = () => {
    const { N, ks } = tirerRecherche();
    // Une égalité par ligne (jamais « ; » entre deux égalités à trous :
    // l'impression coupait la ligne au milieu, convention 2026-09-02).
    const phrase = ks
        .map(k => `${N} = ${k} × {{${N % k === 0 ? N / k : 'impossible'}}}`)
        .join('\n');
    const candidats = ks
        .filter(k => N % k === 0)
        .flatMap(k => [String(N / k + 1), String(N / k - 1)]);
    const payload = makeTexteTrou(phrase,
        `<p>On cherche les diviseurs de ${N}. <b>Complète chaque égalité par un nombre entier, ou par « impossible ».</b></p>`,
        { ...opts(L_RECHERCHE), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, candidats).slice(0, 3) });
    payload._meta.params = { N, ks };
    return payload;
};

/** F2 — V/F : « N = k × un nombre entier » (2 possibles + 2 impossibles). */
const genRechercheVf = () => {
    const { N, poss, imp } = tirerRecherche();
    const lignes = shuffle([
        ...shuffle(poss).slice(0, 2).map(k => [`${N} = ${k} × un nombre entier`, 0]),
        ...shuffle(imp).slice(0, 2).map(k => [`${N} = ${k} × un nombre entier`, 1]),
    ]);
    const payload = makeVraiFaux(
        lignes.map(l => l[0]),
        lignes.map(l => l[1]),
        `<p>On cherche les diviseurs de ${N}. <b>Pour chaque égalité, peut-on la compléter par un nombre entier ?</b></p>`,
        opts(L_RECHERCHE));
    payload._meta.params = { N };
    return payload;
};

// Pool pour « à quelle ligne s'arrêter » : le pivot k×q (q ≤ k) doit exister
// parmi les lignes testables (k ≤ 9).
// (81 exclu : son pivot est 9 × 9, le distracteur « tester jusqu'à 9 »
// deviendrait défendable — question indécidable.)
const POOL_ARRET = [16, 20, 24, 25, 30, 36, 42, 48, 49, 54, 56, 63, 64, 72];

/** Première ligne k × q (k diviseur, q = N/k ≤ k) : le pivot de la recherche. */
const ligneArret = (N) => {
    const k = diviseursDe(N).find(d => N / d <= d);
    return { k, q: N / k };
};

/** F3 — QCM : à quelle ligne peut-on s'arrêter ? (ouverte → fermée, cf. doc) */
const genArretQcm = () => {
    const N = randomChoice(POOL_ARRET);
    const { k, q } = ligneArret(N);
    const question = randomChoice([
        `On cherche tous les diviseurs de ${N} en testant, dans l'ordre, ${N} = 1 × … ; ${N} = 2 × … ; etc. À quelle ligne peut-on s'arrêter ?`,
        `Pour trouver tous les diviseurs de ${N}, on teste les égalités ${N} = 1 × … ; ${N} = 2 × … ; etc. Quelle est la dernière ligne utile ?`,
    ]);
    const correct = `${N} = ${k} × ${q} : au-delà, on retrouverait des facteurs déjà obtenus.`;
    const distracteurs = [
        'Il faut tester tous les nombres jusqu\'à 9.',
        `Il faut tester jusqu'à la moitié de ${N}.`,
        'On peut s\'arrêter dès qu\'une égalité est impossible.',
    ];
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p><b>${question}</b></p>`, opts(L_RECHERCHE));
    payload._meta.params = { N, k, q };
    return payload;
};

// Pool listes complètes : ≤ 9 diviseurs pour rester lisible.
const POOL_LISTE = [36, 42, 54, 63, 66, 70, 105];

/** F4 — Saisie : la liste complète ordonnée, trous partiels (toujours le dernier). */
const genListeTrous = () => {
    const N = randomChoice(POOL_LISTE);
    const divs = diviseursDe(N);
    const trous = new Set([divs.length - 1]); // le dernier (N) est toujours à trouver
    const internes = [];
    for (let i = 1; i < divs.length - 1; i++) internes.push(i);
    shuffle(internes).slice(0, Math.max(2, Math.floor(internes.length / 2))).forEach(i => trous.add(i));
    const phrase = `Tous les diviseurs de ${N}, du plus petit au plus grand : `
        + divs.map((d, i) => (trous.has(i) ? `{{${d}}}` : String(d))).join(' ; ') + '.';
    const payload = makeTexteTrou(phrase,
        `<p><b>Complète la liste de tous les diviseurs de ${N}.</b></p>`,
        { ...opts(L_RECHERCHE), mode: 'saisie' });
    payload._meta.params = { N };
    return payload;
};

/** F5 — QCM multiple : cocher les trois diviseurs (compte annoncé). */
const genListeQcmMultiple = () => {
    const N = randomChoice(POOL_LISTE);
    const divs = diviseursDe(N);
    const milieux = divs.slice(1, -1); // ni 1 ni N (trop faciles)
    const vrais = shuffle(milieux).slice(0, 3).map(String);

    const faux = [];
    const candidats = shuffle([
        ...milieux.map(d => d + 1), ...milieux.map(d => d - 1),
        N - 1, 11, 13, 17, 23,
    ]);
    for (const c of candidats) {
        if (faux.length >= 3) break;
        if (c >= 2 && N % c !== 0 && !faux.includes(String(c)) && !vrais.includes(String(c))) {
            faux.push(String(c));
        }
    }
    const payload = makeQcm([...vrais, ...faux], vrais,
        `<p><b>Coche les trois nombres qui sont des diviseurs de ${N}.</b></p>`,
        opts(L_RECHERCHE), true);
    payload._meta.params = { N, vrais };
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE 2 — Compter les diviseurs (Ex.2)
// ═════════════════════════════════════════════════════════════

// Le pool contient les carrés (16, 25, 36, 49) : nombre IMPAIR de diviseurs.
const POOL_COMPTAGE = [8, 10, 12, 15, 16, 18, 20, 21, 25, 27, 28, 32, 36, 45, 49];

const questionComptage = (n) => randomChoice([
    `Combien de diviseurs possède ${n} ?`,
    `Donne le nombre de diviseurs de ${n}.`,
]);

/** F6 — Saisie du nombre de diviseurs. */
const genComptageSaisie = () => {
    const n = randomChoice(POOL_COMPTAGE);
    const payload = makeSaisie(String(diviseursDe(n).length),
        `<p><b>${questionComptage(n)}</b></p>`, opts(L_COMPTAGE));
    payload._meta.params = { n };
    return payload;
};

/** F7 — QCM : misconceptions (oubli de 1 et n, oubli de n, racine doublée). */
const genComptageQcm = () => {
    const n = randomChoice(POOL_COMPTAGE);
    const c = diviseursDe(n).length;
    const distracteurs = [];
    [c - 2, c - 1, c + 1, c + 2].forEach(v => {
        if (distracteurs.length < 3 && v >= 1 && v !== c && !distracteurs.includes(String(v))) {
            distracteurs.push(String(v));
        }
    });
    const payload = makeQcm([String(c), ...distracteurs], String(c),
        `<p><b>${questionComptage(n)}</b></p>`, opts(L_COMPTAGE));
    payload._meta.params = { n, c };
    return payload;
};

// Étayage : listes courtes uniquement (≤ 6 diviseurs) — pool élargi (les trous
// étant déterministes, l'unicité = la taille du pool ; 26 valeurs > seuil 20).
const POOL_ETAYE = [
    ...POOL_COMPTAGE.filter(n => diviseursDe(n).length <= 6),
    14, 22, 26, 33, 35, 39, 44, 50, 55, 63, 75, 77,
];

/** F8 — Étayage (cité par le doc) : lister puis compter, en saisie. */
const genComptageEtaye = () => {
    const n = randomChoice(POOL_ETAYE);
    const divs = diviseursDe(n);
    const phrase = `Diviseurs de ${n} : `
        + divs.map((d, i) => (i === 0 || i === divs.length - 1 ? String(d) : `{{${d}}}`)).join(' ; ')
        + `.\nIl y en a donc {{${divs.length}}} en tout.`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Complète la liste des diviseurs de ${n}, puis compte-les.</b></p>`,
        { ...opts(L_COMPTAGE), mode: 'saisie' });
    payload._meta.params = { n };
    return payload;
};

/** F9 — Relier : nombre ↔ « k diviseurs » (comptes deux à deux distincts). */
const genComptageRelier = () => {
    const parCompte = new Map();
    POOL_COMPTAGE.forEach(n => {
        const c = diviseursDe(n).length;
        if (!parCompte.has(c)) parCompte.set(c, []);
        parCompte.get(c).push(n);
    });
    const comptes = shuffle([...parCompte.keys()]).slice(0, 3);
    const pairs = comptes.map(c => ({
        source: String(randomChoice(parCompte.get(c))),
        target: `${c} diviseurs`,
    }));
    const payload = makeRelier(pairs,
        '<p><b>Relie chaque nombre à son nombre de diviseurs.</b></p>',
        opts(L_COMPTAGE));
    payload._meta.params = { comptes };
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE 3 — Division euclidienne avec un tableur (Ex.3, adapté fermé)
// ═════════════════════════════════════════════════════════════

/** Division construite : a = b×q + r, 1 ≤ r < b — vraie par construction. */
const tirerDivision = () => {
    const b = randInt(12, 49);
    const q = randInt(11, 29);
    const r = randInt(1, b - 1);
    return { a: b * q + r, b, q, r };
};

/** Feuille de calcul reproduite en HTML (pattern gen_675). */
const tableTableur = (a, b, c2, d2) =>
    '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;margin:8px auto;text-align:center">'
    + '<tr><th></th><th>A</th><th>B</th><th>C</th><th>D</th></tr>'
    + '<tr><td><b>1</b></td><td>Dividende</td><td>Diviseur</td><td>Quotient</td><td>Reste</td></tr>'
    + `<tr><td><b>2</b></td><td>${a}</td><td>${b}</td><td>${c2}</td><td>${d2}</td></tr>`
    + '</table>';

/** F10 — Saisie : l'égalité de la division euclidienne. */
const genTableurEgalite = () => {
    const { a, b, q, r } = tirerDivision();
    const phrase = `${a} = {{${b}}} × {{${q}}} + {{${r}}}`;
    const payload = makeTexteTrou(phrase,
        `<p>On étudie la division euclidienne de ${a} par ${b} (le reste doit rester plus petit que le diviseur).</p>`
        + tableTableur(a, b, '', '')
        + '<p><b>Complète l\'égalité de la division euclidienne.</b></p>',
        { ...opts(L_TABLEUR), mode: 'saisie' });
    payload._meta.params = { a, b, q, r };
    return payload;
};

/** F11 — QCM : quelle formule en D2 pour le reste ? (les 4 choix du doc) */
const genTableurFormule = () => {
    const { a, b, q } = tirerDivision();
    const correct = '=A2-B2×C2';
    const payload = makeQcm([correct, '=A2-B2', '=A2÷B2', '=B2×C2'], correct,
        `<p>On étudie la division euclidienne de ${a} par ${b}. La cellule C2 contient la formule =QUOTIENT(A2;B2) et affiche ${q}.</p>`
        + tableTableur(a, b, String(q), '')
        + '<p><b>Quelle formule faut-il écrire en D2 pour calculer le reste à partir des cellules précédentes ?</b></p>',
        opts(L_TABLEUR));
    payload._meta.params = { a, b, q };
    return payload;
};

/** F12 — V/F : ce que MOD et QUOTIENT afficheront (valeurs calculées). */
const genTableurModVf = () => {
    const { a, b, q, r } = tirerDivision();
    const modJuste = randomChoice([true, false]);
    const quotJuste = randomChoice([true, false]);
    const xMod = modJuste ? r : r + randInt(1, 4);
    const xQuot = quotJuste ? q : q + (randomChoice([true, false]) ? randInt(1, 3) : -randInt(1, 3));
    const lignes = shuffle([
        [`La formule =MOD(A2;B2) affichera ${xMod}.`, modJuste ? 0 : 1],
        [`La formule =QUOTIENT(A2;B2) affichera ${xQuot}.`, quotJuste ? 0 : 1],
        ['Le reste d\'une division euclidienne est toujours plus petit que le diviseur.', 0],
        [`La formule =MOD(A2;B2) peut afficher un nombre plus grand que ${b}.`, 1],
    ]);
    const payload = makeVraiFaux(
        lignes.map(l => l[0]),
        lignes.map(l => l[1]),
        `<p>Dans cette feuille de calcul, on étudie la division euclidienne de ${a} par ${b}.</p>`
        + tableTableur(a, b, '', '')
        + '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_TABLEUR));
    payload._meta.params = { a, b, q, r, xMod, xQuot };
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE 4 — Pair ou impair : le script (Ex.5)
// ═════════════════════════════════════════════════════════════

const PROMPTS_SCRIPT = [
    'Donne-moi un nombre entier.',
    'Choisis un nombre entier.',
    'Écris un nombre entier.',
];

/** SVG façon blocs Scratch : chapeau, demander, si/sinon (modulo 2), dire ×2. */
const svgScript = (promptTxt) => {
    const W = 570, H = 288;
    const bloc = (x, y, w, h, fill, txt, txtColor = 'white') =>
        `<rect x='${x}' y='${y}' width='${w}' height='${h}' rx='8' fill='${fill}'/>`
        + `<text x='${x + 12}' y='${y + h / 2 + 4}' font-size='13' fill='${txtColor}'>${txt}</text>`;
    return `<svg xmlns='http://www.w3.org/2000/svg' width='${W}' height='${H}' viewBox='0 0 ${W} ${H}' font-family='sans-serif'>`
        + bloc(20, 12, 280, 32, '#FFBF00', 'quand ▶ est cliqué', '#4a3500')
        + bloc(20, 50, 500, 32, '#4C97FF', `demander « ${promptTxt} » et attendre`)
        + bloc(20, 88, 530, 36, '#FFAB19', '')
        + `<text x='32' y='110' font-size='13' fill='white'>si</text>`
        + `<polygon points='74,92 60,106 74,120 226,120 240,106 226,92' fill='#59C059'/>`
        + `<text x='84' y='110' font-size='12.5' fill='white'>réponse modulo 2 = 0</text>`
        + `<text x='252' y='110' font-size='13' fill='white'>alors</text>`
        + `<rect x='20' y='124' width='14' height='42' fill='#FFAB19'/>`
        + bloc(46, 128, 500, 34, '#9966FF', 'dire (regrouper réponse et « est un nombre ............ ») pendant 2 secondes')
        + bloc(20, 170, 530, 26, '#FFAB19', 'sinon')
        + `<rect x='20' y='196' width='14' height='42' fill='#FFAB19'/>`
        + bloc(46, 200, 500, 34, '#9966FF', 'dire (regrouper réponse et « est un nombre ............ ») pendant 2 secondes')
        + bloc(20, 242, 530, 18, '#FFAB19', '')
        + `</svg>`;
};

const elementScript = () => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svgScript(randomChoice(PROMPTS_SCRIPT)))}`, maxWidth: 'compact' } }],
});

/** F13 — Drag : compléter pair / impair (2 formulations). */
const genScriptDrag = () => {
    const prenom = randomChoice(PRENOMS);
    const phrase = randomChoice([
        // Une affirmation par ligne (convention 2026-09-02 : jamais « ; »
        // entre deux trous indépendants, l'impression coupait au milieu).
        'Si le reste de la division de la réponse par 2 vaut 0, le script affiche : « est un nombre {{pair}} ».\nSinon, il affiche : « est un nombre {{impair}} ».',
        'Quand la réponse modulo 2 vaut 0, le script affiche « {{pair}} ».\nQuand elle vaut 1, il affiche « {{impair}} ».',
    ]);
    const payload = makeTexteTrou(phrase,
        `<p>${prenom} a écrit ce script. <b>Complète la description de ce qu'il affiche.</b></p>`,
        {
            ...opts(L_SCRIPT), mode: 'drag',
            distracteurs: filtrerDistracteurs(phrase, ['positif', 'entier', 'décimal']),
            elements_additionnels: elementScript(),
        });
    payload._meta.params = { prenom };
    return payload;
};

/** F14 — V/F : exécutions du script (mélange garanti par construction). */
const genScriptVf = () => {
    const prenom = randomChoice(PRENOMS);
    const nombres = new Set();
    while (nombres.size < 4) nombres.add(randInt(13, 199));
    const ns = [...nombres];
    const lignes = ns.map((n, i) => {
        // ligne 0 : affirmation vraie ; ligne 1 : fausse ; ensuite : au hasard
        const vraie = i === 0 ? true : i === 1 ? false : randomChoice([true, false]);
        const pariteReelle = n % 2 === 0 ? 'pair' : 'impair';
        const affichee = vraie ? pariteReelle : (pariteReelle === 'pair' ? 'impair' : 'pair');
        return [`Si on entre ${n}, le script affichera « ${n} est un nombre ${affichee} ».`, vraie ? 0 : 1];
    });
    const melangees = shuffle(lignes);
    const payload = makeVraiFaux(
        melangees.map(l => l[0]),
        melangees.map(l => l[1]),
        `<p>${prenom} a écrit ce script. <b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>`,
        { ...opts(L_SCRIPT), elements_additionnels: elementScript() });
    payload._meta.params = { ns };
    return payload;
};

/** F15 — QCM : qu'affiche le script pour un nombre donné ? */
const genScriptQcm = () => {
    const prenom = randomChoice(PRENOMS);
    const n = randInt(13, 199);
    const parite = n % 2 === 0 ? 'pair' : 'impair';
    const autre = parite === 'pair' ? 'impair' : 'pair';
    const correct = `${n} est un nombre ${parite}`;
    const payload = makeQcm(
        [correct, `${n} est un nombre ${autre}`, 'Le script n\'affiche rien.', `${n} est un nombre positif`],
        correct,
        `<p>${prenom} a écrit ce script. On entre ${n}. <b>Que va afficher le script ?</b></p>`,
        { ...opts(L_SCRIPT), elements_additionnels: elementScript() });
    payload._meta.params = { n };
    return payload;
};

// ─── Registre des formats ────────────────────────────────────
const GENERATORS = {
    recherche_texte_trou_drag: genRechercheDrag,
    recherche_vf: genRechercheVf,
    recherche_arret_qcm_unique: genArretQcm,
    recherche_liste_texte_trou_saisie: genListeTrous,
    recherche_liste_qcm_multiple: genListeQcmMultiple,
    comptage_texte: genComptageSaisie,
    comptage_qcm_unique: genComptageQcm,
    comptage_etaye_texte_trou_saisie: genComptageEtaye,
    comptage_relier: genComptageRelier,
    tableur_egalite_texte_trou_saisie: genTableurEgalite,
    tableur_formule_qcm_unique: genTableurFormule,
    tableur_mod_vf: genTableurModVf,
    script_texte_trou_drag: genScriptDrag,
    script_execution_vf: genScriptVf,
    script_execution_qcm_unique: genScriptQcm,
};

const FORMAT_LABELS = {
    texte_trou_drag: 'Drag', vf: 'Vrai/Faux',
    arret_qcm_unique: 'QCM Arrêt', liste_texte_trou_saisie: 'Liste (saisie)',
    liste_qcm_multiple: 'QCM Multi', texte: 'Saisie', qcm_unique: 'QCM',
    etaye_texte_trou_saisie: 'Étayage (saisie)', relier: 'Relier',
    egalite_texte_trou_saisie: 'Égalité (saisie)', formule_qcm_unique: 'QCM Formule',
    mod_vf: 'Vrai/Faux', execution_vf: 'Vrai/Faux', execution_qcm_unique: 'QCM',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'recherche', label: L_RECHERCHE, formatsList: buildFormats('recherche') },
    { id: 'comptage', label: L_COMPTAGE, formatsList: buildFormats('comptage') },
    { id: 'tableur', label: L_TABLEUR, formatsList: buildFormats('tableur') },
    { id: 'script', label: L_SCRIPT, formatsList: buildFormats('script') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
